import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, TrendingDown, AlertTriangle, Plus } from 'lucide-react';
import { hasPI } from '../../lib/permissions';
import { piApi } from '../../services/piApi';
import '../pi/PIShell.css';

/** RAG threshold helper — returns 'green' | 'amber' | 'red' */
function ragClass(pct) {
  if (pct == null) return '';
  if (pct >= 95) return 'green';
  if (pct >= 75) return 'amber';
  return 'red';
}

function ExitRing({ score }) {
  const color = score >= 70 ? '#16a34a' : score >= 40 ? '#d97706' : '#dc2626';
  const r = 14;
  const circ = 2 * Math.PI * r;
  const fill = (score / 100) * circ;
  return (
    <svg width={36} height={36} viewBox="0 0 36 36" aria-label={`Exit readiness score: ${score}`}>
      <circle cx={18} cy={18} r={r} fill="none" stroke="#f1f5f9" strokeWidth={3} />
      <circle cx={18} cy={18} r={r} fill="none" stroke={color} strokeWidth={3}
        strokeDasharray={`${fill} ${circ}`} strokeLinecap="round"
        transform="rotate(-90 18 18)" />
      <text x={18} y={22} textAnchor="middle" fontSize={9} fontWeight="700" fill={color}>{score}</text>
    </svg>
  );
}

function StatCard({ value, label, subtext, trend }) {
  return (
    <div className="pi-stat-card">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div className="pi-stat-value">{value ?? '—'}</div>
        {trend != null && (
          <span style={{ fontSize: 12, color: trend >= 0 ? '#16a34a' : '#dc2626', display: 'flex', alignItems: 'center', gap: 3 }}>
            {trend >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
      <div className="pi-stat-label">{label}</div>
      {subtext && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>{subtext}</div>}
    </div>
  );
}

export default function PIPortfolioPage({ currentUser, portcos = [], onSelectPortco }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isLP = currentUser?.role === 'lp';

  useEffect(() => {
    piApi.getDashboardStats()
      .then(r => setData(r.data))
      .catch(e => console.error('[PI Dashboard]', e))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="pi-loading" style={{ padding: 48 }}>
      <div className="pi-spinner" />
      <span>Loading portfolio overview...</span>
    </div>
  );

  // ── LP Restricted View ────────────────────────────────────────────────────
  if (isLP) {
    return (
      <div style={{ padding: '32px 40px', maxWidth: 760 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', marginBottom: 24 }}>Portfolio Overview</h1>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 32 }}>
          <StatCard value={data?.blendedIrr != null ? `${data.blendedIrr}%` : null} label="Fund IRR" />
          <StatCard value={data?.activePortcos} label="Active Portfolio Companies" />
          <StatCard value={data?.lastReportDate || '—'} label="Last LP Report" />
        </div>
        {data?.latestReport && (
          <div className="pi-card">
            <div className="pi-card-header"><span className="pi-card-title">Latest Report</span></div>
            <div className="pi-card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 600, color: '#334155' }}>{data.latestReport.period}</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                  Sent {new Date(data.latestReport.approved_at).toLocaleDateString()}
                </div>
              </div>
              <button className="pi-btn-ghost" onClick={() => navigate('/pi/lp-portal')}>View All Reports</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Full Dashboard ─────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '32px 40px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>Portfolio Dashboard</h1>
          <p style={{ color: '#64748b', fontSize: 13 }}>Overview across all active portfolio companies.</p>
        </div>
        {hasPI(currentUser, 'pi.import') && (
          <button className="pi-btn-ghost" onClick={() => navigate('/pi/import')}>
            <Plus size={14} /> Import
          </button>
        )}
      </div>

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
        <StatCard
          value={data?.blendedIrr != null ? `${data.blendedIrr}%` : null}
          label="Weighted Portfolio IRR"
        />
        <StatCard
          value={data?.activeInitiatives ?? 0}
          label="Active VCP Initiatives"
        />
        <StatCard
          value={data?.activeAddons ?? 0}
          label="Active Add-Ons"
          subtext="Across all platform companies"
        />
        <StatCard
          value={data?.portcosNeedingAttention ?? 0}
          label="Portcos Needing Attention"
        />
      </div>

      {/* Portco table */}
      <div className="pi-card">
        <div className="pi-card-header">
          <span className="pi-card-title">Portfolio Companies</span>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>{portcos.length} companies</span>
        </div>
        {portcos.length === 0 ? (
          <div className="pi-empty">
            <TrendingUp size={32} style={{ opacity: 0.3 }} />
            <div className="pi-empty-title">No portfolio companies yet</div>
            <div className="pi-empty-sub">
              Close a deal in DealNexa to get started — it will appear here automatically.
            </div>
            <button className="pi-btn-ghost" onClick={() => navigate('/deals')}>
              Go to Deals
            </button>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #f1f5f9', background: '#fafbff' }}>
                  {['Company', 'Sector', 'Close Date', 'Hold Period', 'Entry Mult.', 'EBITDA vs Plan', 'IRR Tracking', 'Initiatives', 'Exit Score', 'Status'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: '#64748b', fontWeight: 500, fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {portcos.map(p => {
                  const ebitdaPct = p.underwritten_ebitda && p.actual_ebitda
                    ? Math.round((p.actual_ebitda / p.underwritten_ebitda) * 100)
                    : null;
                  const irrPct = p.target_irr && p.current_irr
                    ? Math.round((p.current_irr / p.target_irr) * 100)
                    : null;
                  const holdMonths = p.close_date
                    ? Math.floor((Date.now() - new Date(p.close_date).getTime()) / (1000 * 60 * 60 * 24 * 30))
                    : null;
                  const holdStr = holdMonths != null
                    ? holdMonths >= 12
                      ? `${Math.floor(holdMonths / 12)}y ${holdMonths % 12}m`
                      : `${holdMonths}m`
                    : '—';

                  return (
                    <tr
                      key={p.id}
                      style={{ borderBottom: '1px solid #f8fafc', cursor: 'pointer', transition: 'background 100ms' }}
                      onClick={() => { if (onSelectPortco) onSelectPortco(p.id); navigate(`/pi/portco/${p.id}/overview`); }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fafbff'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}
                    >
                      <td style={{ padding: '12px 14px', fontWeight: 600, color: '#1e293b' }}>{p.name}</td>
                      <td style={{ padding: '12px 14px', color: '#64748b' }}>{p.sector || '—'}</td>
                      <td style={{ padding: '12px 14px', color: '#64748b', whiteSpace: 'nowrap' }}>
                        {p.close_date ? new Date(p.close_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—'}
                      </td>
                      <td style={{ padding: '12px 14px', color: '#475569' }}>{holdStr}</td>
                      <td style={{ padding: '12px 14px', color: '#475569' }}>{p.entry_multiple != null ? `${p.entry_multiple}x` : '—'}</td>
                      <td style={{ padding: '12px 14px' }}>
                        {ebitdaPct != null ? (
                          <span className={`pi-badge pi-badge-${ragClass(ebitdaPct)}`} aria-label={`EBITDA vs plan: ${ebitdaPct}%`}>
                            {ebitdaPct}%
                          </span>
                        ) : '—'}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        {irrPct != null ? (
                          <span className={`pi-badge pi-badge-${ragClass(irrPct)}`} aria-label={`IRR tracking: ${irrPct}% of target`}>
                            {p.current_irr != null ? `${p.current_irr}%` : '—'}
                          </span>
                        ) : '—'}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        {p.initiatives_complete != null && p.initiatives_total != null ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ flex: 1, height: 5, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden', minWidth: 50 }}>
                              <div style={{
                                height: '100%', borderRadius: 3,
                                width: `${p.initiatives_total ? (p.initiatives_complete / p.initiatives_total) * 100 : 0}%`,
                                background: '#4f46e5',
                              }} />
                            </div>
                            <span style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
                              {p.initiatives_complete}/{p.initiatives_total}
                            </span>
                          </div>
                        ) : '—'}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <ExitRing score={p.exit_readiness_score || 0} />
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        {p.status === 'active' && p.exit_readiness_score >= 70
                          ? <span className="pi-badge pi-badge-green">Exit Ready</span>
                          : p.status === 'active'
                            ? <span className="pi-badge pi-badge-blue">Active</span>
                            : p.status === 'exited'
                              ? <span className="pi-badge pi-badge-purple">Exited</span>
                              : <span className="pi-badge pi-badge-amber">Continuation</span>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
